// UserManagement — CRUD for admin accounts (super_admin only).
import React, { useEffect, useState } from 'react';
import { Activity, Check, Clock, Edit2, Loader2, Plus, Shield, Trash2, X, QrCode } from 'lucide-react';
import { apiCall } from './_shared';

const roleColor = (role) => {
  switch (role) {
    case 'super_admin':  return 'bg-red-100 text-red-700';
    case 'admin':        return 'bg-blue-100 text-blue-700';
    case 'manager':      return 'bg-purple-100 text-purple-700';
    case 'reception':    return 'bg-green-100 text-green-700';
    case 'housekeeping': return 'bg-orange-100 text-orange-700';
    case 'maintenance':  return 'bg-yellow-100 text-yellow-700';
    case 'marketing':    return 'bg-pink-100 text-pink-700';
    case 'accountant':   return 'bg-teal-100 text-teal-700';
    case 'bar':          return 'bg-amber-100 text-amber-800';
    case 'waiter':       return 'bg-orange-100 text-orange-800';
    default:             return 'bg-gray-100 text-gray-700';
  }
};

// Full set of sidebar section IDs (matches AdminPanel.jsx tabs[].id).
const ALL_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'reception', label: 'Reception' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'housekeeping', label: 'Housekeeping' },
  { id: 'fb', label: 'F&B Covers' },
  { id: 'bar', label: 'Bar' },
  { id: 'waiter', label: 'Waiter' },
  { id: 'finance', label: 'Finance' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'bi', label: 'Owner BI' },
  { id: 'coupons', label: 'Coupons' },
  { id: 'inroom', label: 'In-Room QR' },
  { id: 'roomhistory', label: 'Room History' },
  { id: 'arrivals', label: 'Arrival Desk' },
  { id: 'emails', label: 'Guest Emails' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'testimonials', label: 'Reviews' },
  { id: 'translations', label: 'Translations' },
  { id: 'conversations', label: 'Conversations' },
  { id: 'users', label: 'Users' },
];

// Built-in defaults per role (fallback when no override exists in Mongo).
const DEFAULT_ROLE_SECTIONS = {
  super_admin:  ALL_SECTIONS.map(s => s.id),
  admin:        ALL_SECTIONS.filter(s => s.id !== 'users').map(s => s.id),
  manager:      ['dashboard','reception','calendar','housekeeping','fb','bar','finance','inbox','bi','coupons','inroom','roomhistory','arrivals','emails','bookings','rooms','gallery','testimonials','conversations'],
  reception:    ['dashboard','reception','calendar','housekeeping','fb','bar','finance','inbox','inroom','roomhistory','arrivals','bookings','conversations'],
  housekeeping: ['dashboard','reception','calendar','housekeeping','inroom','roomhistory','arrivals'],
  maintenance:  ['dashboard','housekeeping','inroom','roomhistory'],
  marketing:    ['dashboard','rooms','gallery','testimonials','conversations','coupons','inbox'],
  accountant:   ['dashboard','bookings','calendar','finance','bi','emails'],
  bar:          ['dashboard','bar'],
  waiter:       ['dashboard','bar'],
};

const ROLE_ORDER = ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'maintenance', 'marketing', 'accountant', 'bar'];

const sectionLabel = (id) => (ALL_SECTIONS.find(s => s.id === id) || { label: id }).label;

import AttendanceCard from './AttendanceCard';
import { StaffBadgeModal } from './StaffBadgeModal';

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [badgeFor, setBadgeFor] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', role: 'admin' });
  // Custom role → sections overrides fetched from Mongo.
  const [overrides, setOverrides] = useState({});
  // Editor state: which role is being edited + current draft selection.
  const [editingRole, setEditingRole] = useState(null);
  const [draftSections, setDraftSections] = useState([]);
  const [savingRole, setSavingRole] = useState(false);
  // Audit-log modal: username or null.
  const [auditUser, setAuditUser] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  // Floors editor (housekeeping routing): username being edited + draft text.
  const [floorsUser, setFloorsUser] = useState(null);
  const [floorsDraft, setFloorsDraft] = useState('');

  const saveFloors = async () => {
    const floors = floorsDraft.split(/[,\s]+/).map((f) => f.trim()).filter(Boolean);
    try {
      await apiCall(`/api/admin/users/${floorsUser}/floors`, { method: 'PUT', body: JSON.stringify({ floors }) });
      setFloorsUser(null);
      await fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  useEffect(() => { fetchUsers(); fetchOverrides(); }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiCall('/api/admin/users');
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await apiCall('/api/admin/role-sections');
      setOverrides(res.overrides || {});
    } catch (err) { /* non-fatal */ }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    const current = overrides[role] || DEFAULT_ROLE_SECTIONS[role] || [];
    setDraftSections([...current]);
  };
  const toggleDraft = (id) => {
    setDraftSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const saveRoleSections = async () => {
    setSavingRole(true);
    try {
      await apiCall(`/api/admin/role-sections/${editingRole}`, {
        method: 'PUT',
        body: JSON.stringify({ sections: draftSections }),
      });
      await fetchOverrides();
      setEditingRole(null);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally { setSavingRole(false); }
  };
  const resetRoleSections = async () => {
    if (!window.confirm(`Reset ${editingRole} to the built-in default sections?`)) return;
    setSavingRole(true);
    try {
      await apiCall(`/api/admin/role-sections/${editingRole}`, { method: 'DELETE' });
      await fetchOverrides();
      setEditingRole(null);
    } catch (err) {
      alert('Reset failed: ' + err.message);
    } finally { setSavingRole(false); }
  };

  const openAudit = async (username) => {
    setAuditUser(username);
    setAuditRows([]);
    setAuditLoading(true);
    try {
      const res = await apiCall(`/api/admin/audit-log?user=${encodeURIComponent(username)}&limit=100`);
      setAuditRows(res.items || []);
    } catch (err) {
      alert('Load activity failed: ' + err.message);
    } finally { setAuditLoading(false); }
  };

  const handleAdd = async () => {
    try {
      await apiCall('/api/admin/users', { method: 'POST', body: JSON.stringify(newUser) });
      await fetchUsers();
      setShowAddModal(false);
      setBadgeFor({ username: newUser.username, justCreated: true });
      setNewUser({ username: '', password: '', email: '', role: 'admin' });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleToggleActive = async (username, isActive) => {
    try {
      await apiCall(`/api/admin/users/${username}?is_active=${!isActive}`, { method: 'PUT' });
      await fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user ${username}?`)) return;
    try {
      await apiCall(`/api/admin/users/${username}`, { method: 'DELETE' });
      await fetchUsers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  // Rows precomputed OUTSIDE JSX (Babel workaround).
  const rows = users.map((user) => (
    <tr key={user.username} className="border-b last:border-0">
      <td className="py-3 px-4 font-medium text-gray-900">{user.username}</td>
      <td className="py-3 px-4 text-gray-600">{user.email || '-'}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColor(user.role)}`}>{user.role}</span>
      </td>
      <td className="py-3 px-4 text-xs" data-testid={`user-floors-${user.username}`}>
        {user.role === 'housekeeping' ? (
          floorsUser === user.username ? (
            <div className="flex items-center gap-1">
              <input
                value={floorsDraft}
                onChange={(e) => setFloorsDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveFloors(); if (e.key === 'Escape') setFloorsUser(null); }}
                placeholder="e.g. 3, 4, 5"
                className="w-24 border rounded px-2 py-1 text-xs"
                autoFocus
                data-testid={`user-floors-input-${user.username}`}
              />
              <button onClick={saveFloors} className="p-1 hover:bg-green-50 rounded" title="Save" data-testid={`user-floors-save-${user.username}`}><Check className="w-3.5 h-3.5 text-green-600" /></button>
              <button onClick={() => setFloorsUser(null)} className="p-1 hover:bg-gray-100 rounded" title="Cancel"><X className="w-3.5 h-3.5 text-gray-500" /></button>
            </div>
          ) : (
            <button
              onClick={() => { setFloorsUser(user.username); setFloorsDraft((user.floors || []).join(', ')); }}
              className="flex items-center gap-1 text-slate-700 hover:text-orange-600"
              title="Assign floors — pushes for these floors go to this cleaner"
              data-testid={`user-floors-edit-${user.username}`}
            >
              {(user.floors || []).length
                ? (user.floors || []).map((f) => <span key={f} className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">F{f}</span>)
                : <span className="text-gray-400 italic">all floors</span>}
              <Edit2 className="w-3 h-3 text-gray-400" />
            </button>
          )
        ) : <span className="text-gray-300">—</span>}
      </td>
      <td className="py-3 px-4 text-center">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {user.is_active ? 'Active' : 'Disabled'}
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => openAudit(user.username)}
            className="p-1 hover:bg-gray-100 rounded"
            title="View activity"
            data-testid={`user-activity-${user.username}`}
          >
            <Activity className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={() => setBadgeFor({ username: user.username })} className="p-1 hover:bg-gray-100 rounded" title="Personal shift badge (QR)" data-testid={`user-badge-${user.username}`}>
            <QrCode className="w-4 h-4 text-slate-700" />
          </button>
          {user.username !== currentUser.username && (
            <>
              <button onClick={() => handleToggleActive(user.username, user.is_active)} className="p-1 hover:bg-gray-100 rounded" title={user.is_active ? 'Disable' : 'Enable'}>
                {user.is_active ? <X className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-green-500" />}
              </button>
              <button onClick={() => handleDelete(user.username)} className="p-1 hover:bg-gray-100 rounded" title="Delete">
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  ));

  return (
    <div className="space-y-6">
      <AttendanceCard canManage={['super_admin', 'admin', 'manager'].includes(currentUser?.role)} />
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Username</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Floors</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

      {/* Role permissions — editable sidebar map. Overrides live in Mongo;
          without an override each role falls back to the built-in defaults. */}
      <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="role-permissions-card">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Role permissions</h3>
          <span className="text-xs text-gray-500">controls the sidebar — backend permissions are still enforced separately</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ROLE_ORDER.map((r) => {
            const hasOverride = Array.isArray(overrides[r]);
            const effective = hasOverride ? overrides[r] : (DEFAULT_ROLE_SECTIONS[r] || []);
            const canEdit = r !== 'super_admin';
            return (
              <div key={r} className="border rounded-lg p-3" data-testid={`role-card-${r}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${roleColor(r)}`}>{r}</span>
                  <span className="text-xs text-gray-500">{effective.length} sections</span>
                  {hasOverride && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-500 text-white px-1.5 py-0.5 rounded">custom</span>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => openEditRole(r)}
                      className="ml-auto p-1 hover:bg-orange-50 rounded"
                      title="Edit sections"
                      data-testid={`role-edit-${r}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 text-orange-500" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {effective.map((id) => (
                    <span
                      key={id}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
                    >
                      {sectionLabel(id)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Role editor modal */}
      {editingRole && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !savingRole && setEditingRole(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="role-editor-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h4 className="text-lg font-semibold text-gray-900">Edit <span className={`px-2 py-0.5 rounded-full text-xs ${roleColor(editingRole)}`}>{editingRole}</span> sidebar</h4>
              <button onClick={() => setEditingRole(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <p className="text-xs text-gray-500 mb-3">Toggle a section to control what shows in the sidebar for every user with this role. Backend endpoint permissions are unaffected.</p>
              <div className="grid grid-cols-2 gap-2">
                {ALL_SECTIONS.map((s) => {
                  const checked = draftSections.includes(s.id);
                  return (
                    <label key={s.id} className={`flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer ${checked ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDraft(s.id)}
                        data-testid={`role-edit-toggle-${editingRole}-${s.id}`}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-800">{s.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between border-t px-5 py-3 bg-gray-50 rounded-b-xl">
              <button onClick={resetRoleSections} disabled={savingRole || !overrides[editingRole]} className="text-xs text-gray-600 hover:text-red-600 disabled:opacity-40" data-testid="role-edit-reset">Reset to default</button>
              <div className="flex gap-2">
                <button onClick={() => setEditingRole(null)} className="px-3 py-1.5 text-sm rounded border">Cancel</button>
                <button onClick={saveRoleSections} disabled={savingRole} className="px-3 py-1.5 text-sm rounded bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60" data-testid="role-edit-save">
                  {savingRole ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit log modal — per-user recent activity */}
      {auditUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAuditUser(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="audit-log-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-orange-500" />
                Activity for <span className="text-orange-600">{auditUser}</span>
              </h4>
              <button onClick={() => setAuditUser(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {auditLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
              ) : auditRows.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-8">No recent activity for {auditUser}.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="py-1">When</th>
                      <th className="py-1">Method</th>
                      <th className="py-1">Path</th>
                      <th className="py-1 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody data-testid="audit-log-rows">
                    {auditRows.map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-1.5 text-xs text-gray-600 whitespace-nowrap">
                          <Clock className="inline w-3 h-3 mr-1 text-gray-400" />
                          {String(row.at).slice(0, 19).replace('T', ' ')}
                        </td>
                        <td className="py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            row.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                            row.method === 'POST' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>{row.method}</span>
                        </td>
                        <td className="py-1.5 text-xs font-mono text-gray-700 truncate max-w-xs">{row.path}</td>
                        <td className="py-1.5 text-xs text-center text-gray-600">{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {badgeFor && <StaffBadgeModal username={badgeFor.username} justCreated={badgeFor.justCreated} canRotate={['super_admin', 'admin', 'manager'].includes(currentUser?.role)} onClose={() => setBadgeFor(null)} />}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-semibold mb-4">Add New User</h3>
            <div className="space-y-4">
              <input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Username"
              />
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Password"
              />
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Email (optional)"
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="user-role-select"
              >
                <option value="super_admin">Super Admin — Full access + user management</option>
                <option value="admin">Admin — Everything except Users</option>
                <option value="manager">Manager — Everything except Users & Translations</option>
                <option value="reception">Reception — Today, folios, calendar</option>
                <option value="housekeeping">Housekeeping — Room status & tickets (read Today)</option>
                <option value="maintenance">Maintenance — Tickets + in-room QR requests</option>
                <option value="marketing">Marketing — Rooms, gallery, reviews, coupons</option>
                <option value="accountant">Accountant — Bookings, Owner BI, emails</option>
                <option value="bar">Bar — POS & stock only</option>
              </select>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={handleAdd} className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Create User</button>
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
